/**
 * Age math — shared between web (display) and api (presumed DOB + age refresh).
 * Framework-agnostic: no React, no DOM, no Node-only APIs.
 */
/**
 * Compute age in { years, months } from a DOB string (ISO or any Date.parseable form).
 * Returns null when dob is null/empty/unparseable. Clamps future-dated DOB to { 0, 0 }.
 */
export function computeAgeYm(dob, now = new Date()) {
    if (!dob)
        return null;
    const d = new Date(dob);
    if (Number.isNaN(d.getTime()))
        return null;
    let years = now.getFullYear() - d.getFullYear();
    let months = now.getMonth() - d.getMonth();
    if (months < 0) {
        years -= 1;
        months += 12;
    }
    // If the day hasn't elapsed yet this month, subtract one month.
    if (now.getDate() < d.getDate()) {
        months -= 1;
        if (months < 0) {
            years -= 1;
            months += 12;
        }
    }
    if (years < 0)
        return { years: 0, months: 0 };
    return { years, months };
}
/**
 * Convert an age expressed in years + months into a presumed ISO DOB (YYYY-MM-DD).
 * The DOB day is normalized to the 1st of the computed birth month so the
 * resulting age is stable (re-computing the age from the presumed DOB on the
 * same `now` returns the same { years, months } the user entered, modulo
 * day-of-month edge cases which we avoid by pinning day=1).
 */
export function presumedDobFromAge(ageYears, ageMonths, now = new Date()) {
    let birthYear = now.getFullYear() - ageYears;
    let birthMonth0 = now.getMonth() - ageMonths;
    if (birthMonth0 < 0) {
        birthYear -= 1;
        birthMonth0 += 12;
    }
    // Pin day to 1 — avoids the user's age ticking over month-to-month.
    const d = new Date(birthYear, birthMonth0, 1);
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
}
/**
 * Format an AgeYm (or null) as a short label.
 * - null        → ""
 * - 0y 0m       → "0y"
 * - 0y 8m       → "8m"
 * - 2y 0m       → "2y"
 * - 2y 3m       → "2y 3m"
 */
export function formatAgeLabel(age) {
    if (!age)
        return "";
    if (age.years === 0 && age.months > 0)
        return `${age.months}m`;
    if (age.months === 0)
        return `${age.years}y`;
    return `${age.years}y ${age.months}m`;
}
//# sourceMappingURL=age.js.map